import { Download, FileText, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFileIcon, timeAgo } from "@/components/project-hub/utils";
import { useToast } from "@/components/shared";
import { buildChatSessionPath } from "@/lib/chatNavigation";
import { downloadAttachment } from "@/lib/downloadAttachment";
import { projectService } from "@/lib/services";
import type { ProjectArtifact } from "@/types";

// ============================================================================
// ARTIFACTS TAB — AI-generated files from this project's conversations
// ============================================================================

export function ArtifactsTab({
  projectId,
  projectUuid,
  navigate,
}: Readonly<{
  projectId: string;
  projectUuid: string;
  navigate: ReturnType<typeof useNavigate>;
}>) {
  const { showToast } = useToast();
  const [artifacts, setArtifacts] = useState<ProjectArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<{ total: number; totalPages: number } | null>(null);
  const pageSize = 20;

  const loadArtifacts = useCallback(
    async (targetPage: number) => {
      try {
        setLoading(true);
        const response = await projectService.getArtifacts(projectId, { page: targetPage, pageSize });
        setArtifacts(response.data);
        setMeta({ total: response.meta.total, totalPages: response.meta.totalPages });
        setPage(targetPage);
      } catch (err) {
        console.error("Failed to load artifacts:", err);
        showToast("Failed to load artifacts", "error");
      } finally {
        setLoading(false);
      }
    },
    [projectId, showToast],
  );

  useEffect(() => {
    loadArtifacts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleDownload = async (artifact: ProjectArtifact) => {
    try {
      await downloadAttachment({ attachmentUuid: artifact.uuid }, artifact.filename);
    } catch (err) {
      console.error("Failed to download artifact:", err);
      showToast("Failed to download file", "error");
    }
  };

  if (loading && artifacts.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!loading && artifacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-brand-primary" />
        </div>
        <h3 className="text-lg font-medium text-content-primary">No artifacts yet</h3>
        <p className="text-content-tertiary mt-1 text-center max-w-sm">
          Documents, spreadsheets, and other files generated in this project&apos;s conversations will show up here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
        {artifacts.map((artifact, idx) => {
          const Icon = getFileIcon(artifact.mimeType);
          return (
            <div
              key={artifact.uuid}
              className={`flex items-center gap-4 px-5 py-3.5 hover:bg-interactive-hover transition-colors ${
                idx < artifacts.length - 1 ? "border-b border-border-primary" : ""
              }`}
            >
              <div className="h-9 w-9 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
                <Icon className="h-4 w-4 text-brand-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-content-primary truncate">{artifact.filename}</p>
                  {artifact.isRevision && artifact.version && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary font-medium shrink-0">
                      v{artifact.version}
                    </span>
                  )}
                </div>
                <p className="text-xs text-content-quaternary mt-0.5 truncate">
                  {artifact.createdByName && `${artifact.createdByName} · `}
                  from &quot;{artifact.sessionTitle || "Untitled conversation"}&quot; · {timeAgo(artifact.createdAt)}
                </p>
              </div>
              <button
                onClick={() => navigate(buildChatSessionPath(artifact.sessionUuid, projectUuid))}
                className="text-xs font-medium text-brand-primary hover:text-brand-primary/80 shrink-0 px-2 py-1.5 rounded-lg hover:bg-brand-primary/10 transition-colors"
              >
                View in chat
              </button>
              <button
                onClick={() => handleDownload(artifact)}
                className="p-1.5 text-content-quaternary hover:text-content-primary transition-colors shrink-0"
                title="Download"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-content-tertiary px-1">
          <span>
            Page {page} of {meta.totalPages} ({meta.total} artifact{meta.total === 1 ? "" : "s"})
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadArtifacts(page - 1)}
              disabled={page <= 1}
              className="px-3 py-1.5 bg-surface-secondary hover:bg-interactive-active rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => loadArtifacts(page + 1)}
              disabled={page >= meta.totalPages}
              className="px-3 py-1.5 bg-surface-secondary hover:bg-interactive-active rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
